import React, { ComponentProps, useCallback } from 'react';
import { Controller, FieldValues } from 'react-hook-form';

import { Select } from '@sbv-clients/ui-shared';
import { Density } from '@sbv-clients/util-misc';

import useAutoSelectIfEmpty from '../../hooks/use-auto-select-if-empty';
import { useFormFieldError } from '../../hooks/use-field-error';

type FormSelectFieldProps<T> = ComponentProps<typeof Select> & {
  name?: string;
  autoSelectIfEmpty?: boolean;
  hideStartAdornment?: boolean;
  density?: Density;
};

export const FormSelectField = <T,>({
  name = '',
  options,
  onChange,
  onBlur,
  onOpen,
  autoSelectIfEmpty,
  ...rest
}: FormSelectFieldProps<T>) => {
  useAutoSelectIfEmpty(name, options, autoSelectIfEmpty);
  const { error, helperText } = useFormFieldError(name);

  const render = useCallback(
    ({ field }: { field: FieldValues }) => (
      <Select
        {...rest}
        name={name}
        variant="outlined"
        onOpen={onOpen}
        value={field.value ?? null}
        onChange={(event, child, meta) => {
          field.onChange(event);
          onChange?.(event, child, meta);
        }}
        onBlur={(event) => {
          field.onBlur();
          onBlur?.(event);
        }}
        error={error}
        options={options}
        helperText={helperText}
      />
    ),
    [rest, name, onOpen, error, options, helperText, onChange, onBlur]
  );

  return <Controller name={name} render={render} />;
};
